<template>
  <b-container fluid>
    <h1>Data Consent</h1>
    <div v-if="loaded">
      <mio-filters label="Select country:" :options="ConsentData.filters" />
      <mio-output
        title="Consents obtained, plus teacher and student coverage"
        :method="ConsentData.method"
        :messages="ConsentData.messages"
        :tables="ConsentData.tables"
      />
    </div>
    <h2 v-else>Loading...</h2>
  </b-container>
</template>

<script>
import { mapActions } from 'vuex';
import { set, get, validateRow, generateCompleteMonthData } from '@/utils/MioHelpers';
import MioFilters from '@/components/MioFilters';
import MioOutput from '@/components/MioOutput';

export default {
  name: 'MioConsent',
  components: {
    MioOutput,
    MioFilters,
  },
  data() {
    return {
      loaded: false,
    }
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions({
      GET_DATA_CONSENT_MONTH: "mio/GET_DATA_CONSENT_MONTH",
      GET_STUDENT_COUNTS_MONTH: "mio/GET_STUDENT_COUNTS_MONTH",
      GET_TEACHER_COUNTS_MONTH: "mio/GET_TEACHER_COUNTS_MONTH",

    }),
    async getData() {
      try {
        await this.GET_DATA_CONSENT_MONTH();
        await this.GET_STUDENT_COUNTS_MONTH();
        await this.GET_TEACHER_COUNTS_MONTH();

        this.loaded = true;
      } catch (err) {
        console.error(err);
      }
    }
  },
  computed: {
    ConsentData: {
      get() {
        let input = {
          consentByMonth: this.$store.state.mio.dataConsentMonth,
          studentCountsMonth: this.$store.state.mio.studentCountsMonth,
          teacherCountsMonth: this.$store.state.mio.teacherCountsMonth,
        };
        let working = {};
        let output = {
          method: "All consent data comes from a single database table containing teacher, student, parent and trainer consents. Student interactions and percentages come from student attendance records; teacher interactions and percentages come from classroom visits records.",
          messages: [],
        };

        // Get total interactions
        const months = {
          'January': '01',
          'February': '02',
          'March': '03',
          'April': '04',
          'May': '05',
          'June': '06',
          'July': '07',
          'August': '08',
          'September': '09',
          'October': '10',
          'November': '11',
          'December': '12',
        };

        Object.keys(input.studentCountsMonth).forEach(row => {

          // Validate
          let [valid, message, textualMonth, year, studentID, country] = validateRow(input.studentCountsMonth[row], ['month', 'year', 'student_uuid', 'country']);
          if (!valid) {
            output.messages.push(`record is missing some data: ${message}`);
            return;
          }

          let month = `${year}-${months[textualMonth]}`;

          if (!get(working, `previousInteractions.student.${country}.${studentID}`)) {
            set(working, `previousInteractions.student.${country}.${studentID}`, {});
            set(working, `interactions.student.${country}.${month}.${studentID}`, {});
          }
        });

        Object.keys(input.teacherCountsMonth).forEach(row => {

          // Validate
          let [valid, message, month, teacherID, country] = validateRow(input.teacherCountsMonth[row], ['month', 'teacher_uuid', 'country']);
          if (!valid) {
            output.messages.push(`record is missing some data: ${message}`);
            return;
          }

          if (!get(working, `previousInteractions.teacher.${country}.${teacherID}`)) {
            set(working, `previousInteractions.teacher.${country}.${teacherID}`, {});
            set(working, `interactions.teacher.${country}.${month}.${teacherID}`, {});
          }
        });

        Object.keys(input.consentByMonth).forEach(row => {

          // Validate
          let [valid, message, month, country, type, count] = validateRow(input.consentByMonth[row], ['month', 'country', 'type', 'count']);
          if (!valid) {
            output.messages.push(`record is missing some data: ${message}`);
            return;
          }

          if (count == 0) {
            return;
          }

          // Save country in cols
          set(working, `cols.${type}Consents`, {});

          // save count per month-year and country
          set(working, `data.${country}.rows.${month}.${type}Consents`, count, 'add');
          set(working, `data.${country}.yearCounts.${month.substring(0, 4)}.${type}Consents`, count, 'add');

          // add data from total interactions
          if (type == 'teacher' || type == 'student') {
            if (get(working, `interactions.${type}.${country}.${month}`)) {
              set(working, `data.${country}.rows.${month}.${type}Interactions`, Object.keys(working.interactions[type][country][month]).length);
              set(working, `data.${country}.rows.${month}.${type}Percent`, (100 * working.data[country].rows[month][`${type}Consents`] / Object.keys(working.interactions[type][country][month]).length).toFixed(0));
            }
          }
        });

        // Columns for first table
        working.cols = {
          teacherConsents: 'teacher consents',
          teacherInteractions: 'teacher interactions',
          teacherPercent: 'teacher consent %',
          studentConsents: 'student consents',
          studentInteractions: 'student interactions',
          studentPercent: 'student consent %',
          parentConsents: 'parent consents',
          trainerConsents: 'trainer consents',
        };

        // Consent obtained table
        output.tables = Object.keys(working.data).sort().map(country => ({
          title: country,
          cols: Object.values(working.cols),
          rows: Object.keys(working.data[country].rows).reduce((output, month, index, array) => generateCompleteMonthData(output, month, index, array, Object.keys(working.cols), working.data[country].rows, working.data[country].yearCounts), []).reverse(),
        }));

        // Country filters
        output.filters = Object.keys(working.data).sort();

        // console.log(working);
        // console.log(output);
        return output;
      }
    },
  },
};
</script>
